import { UserLevel, type UserType } from 'src/type/user';

import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  Switch,
  Divider,
  Tooltip,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { TransactionType } from 'src/type/transaction';
// import { PositionTxType } from 'src/type/position';
import useProductOptions from 'src/hooks/use-product-options';
import axios, { endpoints } from 'src/utils/axios';
import { useState, useEffect, useCallback } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import { ProductType, ProductPriceLog, ProductQuotaType } from 'src/type/product';
import { Pagination } from 'src/type/utils';
// import { BranchType } from 'src/type/branch';
import { WAITING_PRICE_BUY_BAHT_LIMIT, WAITING_PRICE_SELL_BAHT_LIMIT } from 'src/config-global';
import { convertGold } from 'src/utils/convert';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { ProductRedeemFee } from 'src/type/gold-withdraw';
import { fParams } from 'src/utils/format-params';
import Decimal from 'decimal.js';
import { AmountBreakdownResult, calculateAmountBreakdown } from 'src/utils/amount-breakdown';
import Iconify from 'src/components/iconify';
import PositionQuotasTable from '../../components/position-quotas-table';
import ProductRedeemFeeListDialog from './product-redeem-fee-list-dialog';

const GRAM_MULTIPLIER = new Decimal('0.0656');
const SILVER_GRAM_PER_BAHT = new Decimal('15.244');

const paymentTypeOptions = [
  {
    label: 'โอนเงิน',
    value: 'BANK',
  },
  {
    label: 'เงินสด',
    value: 'CASH',
  },
  {
    label: 'เครดิต',
    value: 'CREDIT',
  },
];

const defaultValues = {
  type: '',
  productId: '',
  amount: 0,
  paymentType: '',
  priceSellPerUnit: 0,
  priceBuyPerUnit: 0,
  enableWaitingPrice: false,
  waitingPrice: 0,
  unitType: 'BAHT',
};

const Schema = Yup.object().shape({
  type: Yup.string().required('กรุณาเลือกประเภท'),
  productId: Yup.string().required('กรุณาเลือกสินค้า'),
  amount: Yup.number().typeError('น้ำหนักต้องเป็นตัวเลข').required('จำเป็นต้องระบุน้ำหนัก'),
  paymentType: Yup.string().when('type', {
    is: TransactionType.BUY_POSITION,
    then: (schema) => schema.required('กรุณาเลือกช่องทางการชำระเงิน'),
    otherwise: (schema) => schema.notRequired(),
  }),
  priceSellPerUnit: Yup.number().notRequired(),
  priceBuyPerUnit: Yup.number().notRequired(),
  enableWaitingPrice: Yup.boolean(),
  waitingPrice: Yup.number().when('enableWaitingPrice', {
    is: true,
    then: (schema) => schema.required('กรุณากรอกราคาเป้าหมาย'),
    otherwise: (schema) => schema.notRequired(),
  }),
  unitType: Yup.string().required('กรุณาเลือกหน่วยของทองคำ'),
});

const typeOptions = [
  {
    value: TransactionType.BUY_POSITION,
    label: 'ตัดซื้อ',
  },
  {
    value: TransactionType.SELL_POSITION,
    label: 'ตัดขาย',
  },
];

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType;
  handleReload: VoidFunction;
};

export default function PositionCreateDialog({ open, onClose, user, handleReload }: Props) {
  const currentDateTime = useCurrentDateTime();

  const amountError = useBoolean();
  const loading = useBoolean(true);
  const enableWaitingPrice = useBoolean(false);
  const [products, setProducts] = useState<ProductType[]>([]);
  const [productRedeemFees, setProductRedeemFees] = useState<ProductRedeemFee[]>([]);
  const waitingPriceError = useBoolean(false);
  const helpDialogOpen = useBoolean(false);
  const breakdownConfirmOpen = useBoolean(false);
  const [breakdownResult, setBreakdownResult] = useState<AmountBreakdownResult | null>(null);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<any>(null);
  const [isSubmittingBreakdown, setIsSubmittingBreakdown] = useState(false);
  const isDevSubmitting = useBoolean(false);

  const [priceSellPerUnit, setPriceSellPerUnit] = useState<number | null>(null);
  const [priceBuyPerUnit, setPriceBuyPerUnit] = useState<number | null>(null);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  // Get amount directly from form
  const currentAmount = Number(watch().amount || 0);

  const handleAmountInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: originalValue } = event.target;
    let value = originalValue;
    if (value && value.startsWith('.')) {
      value = `0${value}`;
    }
    methods.setValue('amount', value as unknown as number, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const { productOptions } = useProductOptions(true, user.uid);
  const selectedProduct = productOptions.find((item) => item.value === watch().productId);
  const isBar96 =
    selectedProduct &&
    selectedProduct?.label.includes('แท่ง') &&
    selectedProduct?.label.includes('96');

  // Validate quota limit
  useEffect(() => {
    if (currentAmount && watch().type && watch().productId) {
      const mappedQuota = user?.userProductPositionQuotas?.map((quota) => {
        if (quota.type === ProductQuotaType.BUY) {
          return {
            ...quota,
            txType: TransactionType.BUY_POSITION,
          };
        }
        if (quota.type === ProductQuotaType.SELL) {
          return {
            ...quota,
            txType: TransactionType.SELL_POSITION,
          };
        }
        return quota;
      });

      const quota = mappedQuota?.find(
        (q) => q.productId === watch().productId && q.txType === watch().type
      );

      // Check quota limit (skip for LV0 users)
      const exceedsQuota =
        user.level !== UserLevel.LV0 &&
        currentAmount >
          (watch().unitType === 'GRAM'
            ? Number(quota?.amount!)
            : convertGold(Number(quota?.amount!), 'weightBaht'));

      // Validate amount เฉพาะ quota เท่านั้น (breakdown จะเช็คตอน submit)
      if (exceedsQuota) {
        amountError.onTrue();
      } else {
        amountError.onFalse();
      }
    }
  }, [currentAmount, watch().type, watch().productId, watch().unitType, productRedeemFees]);

  // Validate waiting price realtime
  useEffect(() => {
    if (!enableWaitingPrice.value) {
      waitingPriceError.onFalse();
      return;
    }

    const waitingPriceBuyBahtLimit = Number(`${WAITING_PRICE_BUY_BAHT_LIMIT || 2000}`);
    const waitingPriceSellBahtLimit = Number(`${WAITING_PRICE_SELL_BAHT_LIMIT || 2000}`);

    // ตรวจสอบว่าเป็นสินค้าเงิน (silver) หรือไม่
    const selectedProd = products.find((p) => p.id === watch().productId);
    const isSilverProduct = selectedProd && /เงิน|silver/i.test(selectedProd.name || '');

    // ตั้งราคาเป้าหมาย: ทองใช้หน่วย "ราคาต่อบาท" เสมอ, เงินใช้ "ราคาต่อกิโลกรัม" เสมอ (ไม่ขึ้นกับหน่วยน้ำหนักที่เลือก)
    let convertedPriceSell: number | null = null;
    if (priceSellPerUnit != null) {
      convertedPriceSell = priceSellPerUnit;
    }

    let convertedPriceBuy: number | null = null;
    if (priceBuyPerUnit != null) {
      convertedPriceBuy = priceBuyPerUnit;
    }

    // Silver: limit ±2000 บาท/กก. | Gold: limit ต่อบาทเสมอ (WAITING_PRICE_*_BAHT_LIMIT)
    let waitingPriceBuyLimit: number;
    let waitingPriceSellLimit: number;
    if (isSilverProduct) {
      waitingPriceBuyLimit = 2000;
      waitingPriceSellLimit = 2000;
    } else {
      waitingPriceBuyLimit = waitingPriceBuyBahtLimit;
      waitingPriceSellLimit = waitingPriceSellBahtLimit;
    }

    const waitingPrice = new Decimal(Number(watch().waitingPrice) || 0);

    if (watch().type === TransactionType.BUY_POSITION) {
      const minAllowed = convertedPriceSell
        ? new Decimal(convertedPriceSell).minus(waitingPriceBuyLimit)
        : null;
      const maxAllowed = convertedPriceSell != null ? new Decimal(convertedPriceSell) : null;

      if (
        minAllowed != null &&
        maxAllowed != null &&
        (waitingPrice.lt(minAllowed) || waitingPrice.gt(maxAllowed))
      ) {
        waitingPriceError.onTrue();
      } else {
        waitingPriceError.onFalse();
      }
    } else if (watch().type === TransactionType.SELL_POSITION) {
      const minAllowed = convertedPriceBuy != null ? new Decimal(convertedPriceBuy) : null;
      const maxAllowed = convertedPriceBuy
        ? new Decimal(convertedPriceBuy).plus(waitingPriceSellLimit)
        : null;

      if (
        minAllowed != null &&
        maxAllowed != null &&
        (waitingPrice.lt(minAllowed) || waitingPrice.gt(maxAllowed))
      ) {
        waitingPriceError.onTrue();
      } else {
        waitingPriceError.onFalse();
      }
    } else {
      waitingPriceError.onFalse();
    }
  }, [
    watch().type,
    watch().unitType,
    watch().waitingPrice,
    watch().productId,
    enableWaitingPrice.value,
    priceSellPerUnit,
    priceBuyPerUnit,
    products,
  ]);

  useEffect(() => {
    if (!isBar96) {
      setValue('unitType', 'GRAM');
    }
  }, [isBar96]);

  // Reset amount when unit type changes to prevent calculation bugs
  const handleUnitTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUnitType = event.target.value;
    const currentUnitType = watch().unitType;

    // Only reset if unit type actually changed
    if (newUnitType !== currentUnitType) {
      // Reset amount and related states
      setValue('amount', 0, { shouldValidate: false, shouldDirty: false });
      amountError.onFalse();
    }

    setValue('unitType', newUnitType);
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (amountError.value) return;

      // เช็ค amount breakdown ก่อน submit
      if (productRedeemFees.length > 0 && Number(values.amount) > 0) {
        const calculatedBreakdown = calculateAmountBreakdown(
          Number(values.amount),
          values.unitType as 'BAHT' | 'GRAM',
          productRedeemFees
        );

        // แสดง confirm dialog เสมอ ไม่ว่าจะ valid หรือไม่
        setBreakdownResult(calculatedBreakdown);
        setPendingSubmitValues(values);
        breakdownConfirmOpen.onTrue();
        return;
      }

      // ถ้าไม่มี breakdown config หรือ amount = 0 ให้ส่งไปเลย
      await submitTransaction(values);
    } catch (error) {
      toastError(error);
    }
  });

  const submitTransaction = async (values: any) => {
    try {
      const body: Record<string, any> = {
        uid: user.uid,
        productId: values.productId,
        amount: Number(values.amount),
        ...(enableWaitingPrice.value && { waitingPrice: Number(values.waitingPrice) }),
        currentDateTime,
        unitType: values.unitType,
      };

      let endpoint = '';

      if (values.type === TransactionType.BUY_POSITION) {
        body.paymentType = values.paymentType;
        endpoint = enableWaitingPrice.value
          ? endpoints.transactions.position.waitingBuy
          : endpoints.transactions.position.buy;
        Object.assign(body, { paymentType: values.paymentType });
      } else if (values.type === TransactionType.SELL_POSITION) {
        endpoint = enableWaitingPrice.value
          ? endpoints.transactions.position.waitingSell
          : endpoints.transactions.position.sell;
      } else {
        throw new Error('ไม่สามารถทำรายการได้');
      }

      await axios.post(endpoint, body);
      handleReload();
      beforeClose();
      enqueueSnackbar('สร้างรายการสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  };

  const handleConfirmSubmit = async () => {
    if (pendingSubmitValues && breakdownResult?.isValid) {
      try {
        setIsSubmittingBreakdown(true);
        await submitTransaction(pendingSubmitValues);
        breakdownConfirmOpen.onFalse();
        setPendingSubmitValues(null);
        setBreakdownResult(null);
      } finally {
        setIsSubmittingBreakdown(false);
      }
    }
  };

  const handleCancelSubmit = () => {
    breakdownConfirmOpen.onFalse();
    setPendingSubmitValues(null);
    setBreakdownResult(null);
    setIsSubmittingBreakdown(false);
  };

  // const devBuyAmount = 10;

  // const handleDevBuy = async () => {
  //   try {
  //     isDevSubmitting.onTrue();

  //     // TODO: remove this after testing

  //     // clear all data
  //     await axios.get('/playground/set-all-user-product-amount-to-zero');

  //     const body = {
  //       uid: 'rS3iMKwNOiYMzr6e2MV0m9fbD6k2',
  //       productId: '9FhTZkU6ssH60tPIcB3k',
  //       amount: devBuyAmount,
  //       currentDateTime,
  //       unitType: 'BAHT',
  //       paymentType: 'CASH',
  //     };

  //     // Create buy position
  //     const { data: buyResponse } = await axios.post<{ id: string } | PositionTxType>(
  //       endpoints.transactions.position.buy,
  //       body
  //     );

  //     if (!buyResponse?.id) {
  //       throw new Error('ไม่พบ transaction id จาก response');
  //     }

  //     // Get first branch for approve
  //     const { data: branchData } = await axios.get<Pagination<BranchType>>(endpoints.branch.get, {
  //       params: { limit: 1 },
  //     });

  //     if (!branchData.results || branchData.results.length === 0) {
  //       throw new Error('ไม่พบข้อมูลสาขา');
  //     }

  //     const firstBranchId = branchData.results[0].id;

  //     // Approve buy position
  //     await axios.post(endpoints.transactions.position.buyApprove(buyResponse.id), {
  //       branchId: firstBranchId,
  //       note: '',
  //       currentDateTime,
  //     });

  //     handleReload();
  //     beforeClose();
  //     enqueueSnackbar('สร้างและอนุมัติรายการสำเร็จ (Dev)', { variant: 'success' });
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     isDevSubmitting.onFalse();
  //   }
  // };

  // const handleDevSell = async () => {
  //   try {
  //     isDevSubmitting.onTrue();

  //     const amount = Number(watch().amount) || 1;

  //     const body = {
  //       uid: user.uid,
  //       productId: '9FhTZkU6ssH60tPIcB3k',
  //       amount,
  //       currentDateTime,
  //       unitType: 'BAHT',
  //     };

  //     // Create sell position
  //     await axios.post(endpoints.transactions.position.sell, body);

  //     handleReload();
  //     beforeClose();
  //     enqueueSnackbar(`สร้างรายการขายสำเร็จ (Dev: ${amount})`, { variant: 'success' });
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     isDevSubmitting.onFalse();
  //   }
  // };

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  // fetch product price log
  const getProductPriceLogs = useCallback(async () => {
    try {
      const { data } = await axios.get<Pagination<ProductType>>(endpoints.product.get);
      const productWithPriceLogs = await Promise.all(
        data.results.map(async (product) => {
          const { data: logs } = await axios.get<Pagination<ProductPriceLog>>(
            `${endpoints.product.priceLog}?productId=${product.id}&userId=${user.uid}&limit=1&orderBy=createdAt:desc`
          );
          return { ...product, productPriceLog: logs.results };
        })
      );
      setProducts(productWithPriceLogs);
    } catch (error) {
      toastError(error);
    } finally {
      loading.onFalse();
    }
  }, [toastError]);

  const getProductRedeemFees = useCallback(async () => {
    try {
      const { data } = await axios.get<Pagination<ProductRedeemFee>>(
        endpoints.productRedeemFees.get,
        {
          params: fParams({ productId: watch().productId, limit: 100, orderBy: 'weightGram:asc' }),
        }
      );
      if (data.results.length > 0) {
        setProductRedeemFees(data.results);
      }
    } catch (error) {
      toastError(error);
    }
  }, [toastError, watch().productId]);

  useEffect(() => {
    getProductPriceLogs();
    getProductRedeemFees();
  }, [getProductPriceLogs, getProductRedeemFees]);

  useEffect(() => {
    const selectedProd = products.find((p) => p.id === watch().productId) || null;
    if (selectedProd && selectedProd.productPriceLog && selectedProd.productPriceLog.length > 0) {
      const latestPriceLog = selectedProd.productPriceLog[0]; // ราคาล่าสุด
      const isSilverProduct = selectedProd && /เงิน|silver/i.test(selectedProd.name || '');
      if (isSilverProduct) {
        setPriceSellPerUnit(new Decimal(latestPriceLog.priceSellPerGram).times(1000).toNumber());
        setPriceBuyPerUnit(new Decimal(latestPriceLog.priceBuyPerGram).times(1000).toNumber());
      } else {
        setPriceSellPerUnit(latestPriceLog.priceSellPerUnit);
        setPriceBuyPerUnit(latestPriceLog.priceBuyPerUnit);
      }
    } else {
      setPriceSellPerUnit(null);
      setPriceBuyPerUnit(null);
    }
  }, [watch().productId, watch().unitType, products]);

  const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    if (checked) {
      enableWaitingPrice.onTrue();
    } else {
      enableWaitingPrice.onFalse();
      methods.setValue('waitingPrice', 0);
      waitingPriceError.onFalse();
    }
  };

  const handleHelpClick = () => {
    helpDialogOpen.onTrue();
  };

  return (
    <>
      <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <DialogTitle>สร้างรายการตัดซื้อ/ขาย</DialogTitle>

          <DialogContent>
            <Stack spacing={3} py={1}>
              {user.level !== UserLevel.LV0 && <PositionQuotasTable user={user} />}

              <Divider sx={{ borderStyle: 'dashed' }} />

              <RHFSelect name="type" label="ประเภท" placeholder="เลือกประเภท">
                {typeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFSelect name="productId" label="สินค้า" placeholder="เลือกสินค้า">
                {productOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                <RHFTextField
                  name="priceBuyPerUnit"
                  label="ราคา DDP รับซื้อ"
                  // InputLabelProps={{ shrink: true }}
                  value={priceBuyPerUnit ? priceBuyPerUnit.toLocaleString('en-US') : ''}
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          {(() => {
                            const selectedProd = products.find((p) => p.id === watch().productId);
                            const isSilverProduct =
                              selectedProd && /เงิน|silver/i.test(selectedProd.name || '');
                            return isSilverProduct ? 'THB/กก.' : 'THB';
                          })()}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <RHFTextField
                  name="priceSellPerUnit"
                  label="ราคา DDP ขาย"
                  // InputLabelProps={{ shrink: true }}
                  value={priceSellPerUnit ? priceSellPerUnit.toLocaleString('en-US') : ''}
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          {(() => {
                            const selectedProd = products.find((p) => p.id === watch().productId);
                            const isSilverProduct =
                              selectedProd && /เงิน|silver/i.test(selectedProd.name || '');
                            return isSilverProduct ? 'THB/กก.' : 'THB';
                          })()}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
                <Stack spacing={2} direction="row" alignItems="center" width={1}>
                  <Iconify
                    icon="material-symbols:help-outline"
                    sx={{
                      width: 25,
                      height: 25,
                      minWidth: 25,
                      color: 'primary.main',
                      cursor: 'pointer',
                    }}
                    onClick={handleHelpClick}
                  />
                  <RHFSelect
                    name="unitType"
                    label="หน่วยทองคำ"
                    onChange={handleUnitTypeChange}
                    disabled={!isBar96}
                  >
                    <MenuItem value="BAHT">บาท</MenuItem>
                    <MenuItem value="GRAM">กรัม</MenuItem>
                  </RHFSelect>
                </Stack>

                <RHFTextField
                  name="amount"
                  type="number"
                  label="น้ำหนัก"
                  placeholder="0"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleAmountInput}
                  error={amountError.value}
                  helperText={(() => {
                    // แสดง error message เฉพาะ quota เท่านั้น
                    if (amountError.value) {
                      // ตรวจสอบว่าเกิน quota หรือไม่
                      const mappedQuota = user?.userProductPositionQuotas?.map((quota) => {
                        if (quota.type === ProductQuotaType.BUY) {
                          return {
                            ...quota,
                            txType: TransactionType.BUY_POSITION,
                          };
                        }
                        if (quota.type === ProductQuotaType.SELL) {
                          return {
                            ...quota,
                            txType: TransactionType.SELL_POSITION,
                          };
                        }
                        return quota;
                      });

                      const quota = mappedQuota?.find(
                        (q) => q.productId === watch().productId && q.txType === watch().type
                      );

                      const exceedsQuota =
                        user.level !== UserLevel.LV0 &&
                        currentAmount >
                          (watch().unitType === 'GRAM'
                            ? Number(quota?.amount!)
                            : convertGold(Number(quota?.amount!), 'weightBaht'));

                      if (exceedsQuota) {
                        return 'น้ำหนักเกินกว่าที่สามารถทำรายการได้';
                      }

                      return 'กรุณาตรวจสอบข้อมูล';
                    }

                    return '';
                  })()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          {watch().unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Stack spacing={1} direction="row" alignItems="center">
                <Switch onChange={handleSwitch} sx={{ width: 60 }} />
                <RHFTextField
                  name="waitingPrice"
                  type="number"
                  label={(() => {
                    const selectedProd = products.find((p) => p.id === watch().productId);
                    const isSilverProduct =
                      selectedProd && /เงิน|silver/i.test(selectedProd.name || '');
                    if (isSilverProduct) {
                      return 'ตั้งราคาเป้าหมาย (ต่อ 1 กิโลกรัม)';
                    }
                    return 'ตั้งราคาเป้าหมาย (ต่อ 1 บาท)';
                  })()}
                  placeholder={(() => {
                    if (!enableWaitingPrice.value) return '0';
                    const isBuyPosition = watch().type === TransactionType.BUY_POSITION;
                    const basePrice = isBuyPosition ? priceSellPerUnit : priceBuyPerUnit;
                    const selectedProd = products.find((p) => p.id === watch().productId);
                    const isSilverProduct =
                      selectedProd && /เงิน|silver/i.test(selectedProd.name || '');
                    if (basePrice == null) return '0';

                    const convertedPrice = new Decimal(basePrice);
                    let limit: number;
                    if (isSilverProduct) {
                      limit = 2000;
                    } else if (isBuyPosition) {
                      limit = WAITING_PRICE_BUY_BAHT_LIMIT;
                    } else {
                      limit = WAITING_PRICE_SELL_BAHT_LIMIT;
                    }

                    if (isSilverProduct) {
                      const minPrice = isBuyPosition ? convertedPrice.minus(limit) : convertedPrice;
                      const maxPrice = isBuyPosition ? convertedPrice : convertedPrice.plus(limit);
                      return `${minPrice.toFixed(1)} - ${maxPrice.toFixed(1)}`;
                    }
                    const minPrice = isBuyPosition
                      ? convertedPrice.minus(limit).floor()
                      : convertedPrice.floor();
                    const maxPrice = isBuyPosition
                      ? convertedPrice.floor()
                      : convertedPrice.plus(limit).floor();
                    return `${minPrice.toNumber().toLocaleString()} - ${maxPrice.toNumber().toLocaleString()} บาท`;
                  })()}
                  InputLabelProps={{ shrink: true }}
                  error={waitingPriceError.value}
                  helperText={
                    waitingPriceError.value
                      ? (() => {
                          const isBuyPosition = watch().type === TransactionType.BUY_POSITION;
                          const basePrice = isBuyPosition ? priceSellPerUnit : priceBuyPerUnit;
                          const selectedProd = products.find((p) => p.id === watch().productId);
                          const isSilverProduct =
                            selectedProd && /เงิน|silver/i.test(selectedProd.name || '');

                          const convertedPrice = basePrice != null ? new Decimal(basePrice) : null;
                          if (!convertedPrice) return 'กรุณาเลือกสินค้า';

                          let limit: number;
                          if (isSilverProduct) {
                            limit = 2000;
                          } else if (isBuyPosition) {
                            limit = WAITING_PRICE_BUY_BAHT_LIMIT;
                          } else {
                            limit = WAITING_PRICE_SELL_BAHT_LIMIT;
                          }

                          if (isSilverProduct) {
                            if (isBuyPosition) {
                              const minAllowed = convertedPrice.minus(limit);
                              const maxAllowed = convertedPrice;
                              return `ราคาเป้าหมายต้องไม่ต่ำกว่า ${minAllowed.toFixed(1)} บาท และไม่เกิน ${maxAllowed.toFixed(1)} บาท`;
                            }
                            const minAllowed = convertedPrice;
                            const maxAllowed = convertedPrice.plus(limit);
                            return `ราคาเป้าหมายต้องไม่ต่ำกว่า ${minAllowed.toFixed(1)} บาท และไม่เกิน ${maxAllowed.toFixed(1)} บาท`;
                          }

                          const flooredPrice = convertedPrice.floor();
                          if (isBuyPosition) {
                            const minAllowed = flooredPrice.minus(limit);
                            const maxAllowed = flooredPrice;
                            return `ราคาเป้าหมายต้องไม่ต่ำกว่า ${minAllowed.toNumber().toLocaleString()} บาท และไม่เกิน ${maxAllowed.toNumber().toLocaleString()} บาท`;
                          }
                          const minAllowed = flooredPrice;
                          const maxAllowed = flooredPrice.plus(limit);
                          return `ราคาเป้าหมายต้องไม่ต่ำกว่า ${minAllowed.toNumber().toLocaleString()} บาท และไม่เกิน ${maxAllowed.toNumber().toLocaleString()} บาท`;
                        })()
                      : ''
                  }
                  disabled={!enableWaitingPrice.value}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          THB
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              {watch('type') === TransactionType.BUY_POSITION && (
                <RHFSelect
                  name="paymentType"
                  label="ช่องทางการชำระเงิน"
                  placeholder="เลือกช่องทางการชำระเงิน"
                >
                  {paymentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            {/* <LoadingButton
              variant="contained"
              onClick={handleDevBuy}
              loading={isDevSubmitting.value}
              disabled={isSubmitting || isDevSubmitting.value}
              sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
            >
              dev-buy {devBuyAmount}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleDevSell}
              loading={isDevSubmitting.value}
              disabled={isSubmitting || isDevSubmitting.value || Number(watch().amount) === 0}
              sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
            >
              dev-sell {Number(watch().amount) || 1}
            </LoadingButton> */}
            <Button
              variant="outlined"
              onClick={beforeClose}
              disabled={isSubmitting || isDevSubmitting.value}
            >
              ยกเลิก
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmitting}
              disabled={Number(watch().amount) === 0 || isDevSubmitting.value}
            >
              บันทึก
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>

      {/* Product Redeem Fee List Dialog */}
      <ProductRedeemFeeListDialog
        open={helpDialogOpen.value}
        onClose={helpDialogOpen.onFalse}
        productRedeemFees={productRedeemFees}
      />

      {/* Breakdown Confirm Dialog */}
      <Dialog
        open={breakdownConfirmOpen.value}
        onClose={handleCancelSubmit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ยืนยันการแบ่งน้ำหนัก</DialogTitle>
        <DialogContent>
          <Stack spacing={2} py={1}>
            <Typography variant="body1">น้ำหนักที่กรอกจะถูกแบ่งเป็น:</Typography>
            {breakdownResult && (
              <Stack spacing={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" spacing={4}>
                  {/* ด้านซ้าย: รายการทองแต่ละบล็อค */}
                  <Stack flex={1} spacing={0.5}>
                    {breakdownResult.breakdown.map((item, index) => (
                      <Typography key={item.config.id || index} variant="body2">
                        {item.config.name}{' '}
                        {pendingSubmitValues?.unitType === 'BAHT'
                          ? `${item.config.weightBaht} บาท`
                          : `${item.config.weightGram} กรัม (${item.config.weightBaht} บาท)`}
                      </Typography>
                    ))}
                  </Stack>
                  {/* ด้านขวา: จำนวนบล็อค */}
                  <Stack alignItems="flex-end" spacing={0.5}>
                    {breakdownResult.breakdown.map((item, index) => (
                      <Typography key={item.config.id || index} variant="body2" fontWeight="bold">
                        x {item.count}
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
                {breakdownResult.remainingAmount > 0 && (
                  <Typography variant="body2" color="warning.main" fontWeight="bold" mt={1}>
                    ค่าที่เหลือ: {breakdownResult.remainingAmount}{' '}
                    {pendingSubmitValues?.unitType === 'BAHT' ? 'บาท' : 'กรัม'}
                  </Typography>
                )}

                {/* แสดงราคาและยอดชำระ */}
                {breakdownResult.isValid && (
                  <Stack
                    spacing={1}
                    mt={2}
                    pt={2}
                    sx={{ borderTop: '1px dashed', borderColor: 'divider' }}
                  >
                    {(() => {
                      const isBuyPosition =
                        pendingSubmitValues?.type === TransactionType.BUY_POSITION;
                      const pricePerUnit = isBuyPosition ? priceSellPerUnit : priceBuyPerUnit;
                      const amount = Number(pendingSubmitValues?.amount || 0);
                      const unitType = pendingSubmitValues?.unitType;
                      const selectedProd = products.find(
                        (p) => p.id === pendingSubmitValues?.productId
                      );
                      const isSilverProduct =
                        selectedProd && /เงิน|silver/i.test(selectedProd.name || '');

                      if (pricePerUnit == null) return null;

                      const priceD = new Decimal(pricePerUnit);
                      let total: Decimal;
                      const displayAmount = amount;
                      const displayUnit = unitType === 'BAHT' ? 'บาททอง' : 'กรัม';

                      if (isSilverProduct) {
                        const pricePerGram = priceD.div(1000);
                        if (unitType === 'GRAM') {
                          total = pricePerGram.times(amount);
                        } else {
                          total = pricePerGram.times(amount).times(SILVER_GRAM_PER_BAHT);
                        }
                      } else if (unitType === 'BAHT') {
                        total = priceD.times(amount);
                      } else {
                        total = priceD.mul(GRAM_MULTIPLIER).times(amount);
                      }

                      const flooredTotal = total.floor().toNumber();

                      const calculationTooltip = (() => {
                        if (isSilverProduct) {
                          if (unitType === 'GRAM') {
                            return `สูตร: ราคา/กก. ÷ 1000 × น้ำหนัก(กรัม)\n= ${pricePerUnit.toLocaleString()} ÷ 1000 × ${amount}\n= ${flooredTotal.toLocaleString()} บาท`;
                          }
                          return `สูตร: ราคา/กก. ÷ 1000 × น้ำหนัก(บาท) × 15.244\n= ${pricePerUnit.toLocaleString()} ÷ 1000 × ${amount} × 15.244\n= ${flooredTotal.toLocaleString()} บาท`;
                        }
                        if (unitType === 'BAHT') {
                          return `สูตร: ราคา/บาททอง × จำนวนบาททอง\n= ${pricePerUnit.toLocaleString()} × ${amount}\n= ${flooredTotal.toLocaleString()} บาท`;
                        }
                        return `สูตร: ราคา/บาททอง × 0.0656 × น้ำหนัก(กรัม)\n= ${pricePerUnit.toLocaleString()} × 0.0656 × ${amount}\n= ${flooredTotal.toLocaleString()} บาท`;
                      })();

                      const pricePerUnitDisplay = (() => {
                        if (isSilverProduct) {
                          return `${priceD.toFixed(2)} บาท/กก.`;
                        }
                        if (unitType === 'BAHT') {
                          return `${pricePerUnit.toLocaleString()} บาท/บาททอง`;
                        }
                        return `${priceD.mul(GRAM_MULTIPLIER).floor().toNumber().toLocaleString()} บาท/กรัม`;
                      })();

                      return (
                        <>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              ราคาต่อหน่วย:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {pricePerUnitDisplay}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              น้ำหนักรวม:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {displayAmount.toLocaleString()} {displayUnit}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                color={isBuyPosition ? 'error.main' : 'success.main'}
                              >
                                {isBuyPosition ? 'ยอดที่ต้องชำระ:' : 'ยอดที่ได้รับ:'}
                              </Typography>
                              <Tooltip
                                title={
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                    {calculationTooltip}
                                  </Typography>
                                }
                                arrow
                                placement="top"
                              >
                                <span>
                                  <Iconify
                                    icon="material-symbols:help-outline"
                                    sx={{
                                      width: 18,
                                      height: 18,
                                      color: 'text.secondary',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </Stack>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color={isBuyPosition ? 'error.main' : 'success.main'}
                            >
                              {flooredTotal.toLocaleString()} บาท
                            </Typography>
                          </Stack>
                        </>
                      );
                    })()}
                  </Stack>
                )}
              </Stack>
            )}
            {breakdownResult?.isValid ? (
              <Typography variant="body2" color="text.secondary">
                คุณต้องการดำเนินการต่อหรือไม่?
              </Typography>
            ) : (
              <Typography variant="body2" color="error.main">
                ไม่สามารถแบ่งน้ำหนักได้ กรุณาตรวจสอบข้อมูล
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancelSubmit} disabled={isSubmittingBreakdown}>
            ยกเลิก
          </Button>
          {breakdownResult?.isValid && (
            <LoadingButton
              variant="contained"
              onClick={handleConfirmSubmit}
              loading={isSubmittingBreakdown}
              disabled={isSubmittingBreakdown}
            >
              ยืนยัน
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
